import { Map, fromJS } from 'immutable'
import { createReducer } from 'reducers/utils'
import { removeAll } from 'utils/immutable'
import { LOCATION_CHANGE } from 'connected-react-router'

import {
  SET_SELECTED_ID,
  SET_COMPONENT_META,
  REMOVE_COMPONENT_META,
  SET_IS_EDITING,
  NAVIGATOR_IS_FULLSCREEN,
  NAVIGATOR_RESET,
  SET_HIDE_BREADCRUMB,
} from 'actions/navigator'

import {
  ENTER_SIMULATE_MODE,
  EXIT_SIMULATE_MODE,
} from 'actions/feature/simulation'
import { SET_OUTPUT_FIELD } from 'actions/feature/outputs'
import { SET_FEATURE_FIELD } from 'actions/feature/featureMetadata'
import { SET_QUERY_FIELD } from 'actions/feature/queries'
import { SET_SHOW_DRAWER_MASK, SET_DRAWER_CLOSED } from 'actions/drawer'
import { COMPLEX, SIMPLE, TIMER } from 'utils/constants'
import { isObject } from 'utils/types'

export const initialState = Map({
  selectedId: null,
  isEditing: false,
  isSimulating: false,
  isActionable: false,
  componentMeta: Map(),
  hideBreadcrumb: false,
  isFullscreen: false,
})

const handlers = {
  [SET_SELECTED_ID]: setSelectedId,
  [SET_COMPONENT_META]: setComponentMeta,
  [REMOVE_COMPONENT_META]: removeComponentMeta,
  [SET_IS_EDITING]: setIsEditing,
  [NAVIGATOR_IS_FULLSCREEN]: navigatorIsFullscreen,
  [NAVIGATOR_RESET]: navigatorReset,
  [SET_OUTPUT_FIELD]: updateDescription,
  [SET_FEATURE_FIELD]: updateDescription,
  [SET_QUERY_FIELD]: updateDescription,
  [SET_HIDE_BREADCRUMB]: setHideBreadcrumb,
  [SET_SHOW_DRAWER_MASK]: setHideBreadcrumb,
  [ENTER_SIMULATE_MODE]: enterSimulateMode,
  [EXIT_SIMULATE_MODE]: exitSimulateMode,
  [SET_DRAWER_CLOSED]: handleDrawerClosed,
  [LOCATION_CHANGE]: handleLocationChange,
}

export default createReducer(initialState, handlers)

function exitSimulateMode(state) {
  return state.set('isSimulating', false)
}

const TIMER_AND_COMPLEX = new Set([TIMER, COMPLEX])
const typeIsTimerOrComplex = (type) => TIMER_AND_COMPLEX.has(type)

const coerceSelectedIdForSimulation = (state, id) => {
  const componentMeta = state.get('componentMeta')
  const selectedItemType = componentMeta.getIn([id, 'type'])
  if (!id || typeIsTimerOrComplex(selectedItemType)) {
    return id
  }
  if (selectedItemType === SIMPLE) {
    const selectedItemParentId = componentMeta.getIn([id, 'parentId'])
    const selectedItemparentType = componentMeta.getIn([
      selectedItemParentId,
      'type',
    ])
    if (typeIsTimerOrComplex(selectedItemparentType)) {
      return selectedItemParentId
    }
  }
  return id
}

function enterSimulateMode(state) {
  const selectedId = state.get('selectedId')
  const coercedId = coerceSelectedIdForSimulation(state, selectedId)
  return state.set('selectedId', coercedId).set('isSimulating', true)
}

function setSelectedId(state, { id, componentMeta }) {
  const isSimulating = state.get('isSimulating')
  if (!isSimulating) {
    if (isObject(componentMeta)) {
      const nextState = setComponentMeta(state, {
        id,
        componentMeta,
      })

      return nextState.set('selectedId', id)
    }

    return state.set('selectedId', id)
  }

  const coercedId = coerceSelectedIdForSimulation(state, id)

  return state.set('selectedId', coercedId)
}

function setComponentMeta(state, { id, componentMeta }) {
  const { Component, ...rest } = componentMeta
  return state
    .setIn(['componentMeta', id], fromJS(rest))
    .setIn(['componentMeta', id, 'Component'], Component)
}

function removeComponentMeta(state, { ids }) {
  const transform = removeAll(ids, 'componentMeta')

  return transform(state)
}

function setIsEditing(state, { isEditing }) {
  return state.set('isEditing', isEditing)
}

function handleDrawerClosed(state) {
  return state.set('isFullscreen', false)
}

function handleLocationChange(state) {
  return state.set('isFullscreen', false).set('selectedId', null)
}

function navigatorIsFullscreen(state, { isFullscreen }) {
  return state.set('isFullscreen', isFullscreen)
}

function navigatorReset() {
  return initialState
}

function updateDescription(state, { paramName, value, ...rest }) {
  if (paramName !== 'description') return state

  // ids
  // query = queryUri
  // output = id
  // TODO: talk to adi about making the output give a uri?
  // feature = featureId
  let id
  if (rest.id) {
    id = rest.id
  } else if (rest.queryUri) {
    id = rest.queryUri
  } else if (rest.featureId) {
    id = rest.featureId
  } else return state

  return state.setIn(['componentMeta', id, 'description'], value)
}

function setHideBreadcrumb(state, { hideBreadcrumb, visible }) {
  return state.set('hideBreadcrumb', hideBreadcrumb || visible)
}
